import React from 'react';
import ConfiguredItemArt from './configurator/ConfiguredItemArt';
import {ProductVisual} from './ProductMedia';
import {productMedia} from '../utils/productMedia';
import { Link } from 'react-router-dom';
import { ShoppingBag, Minus, Plus, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { useCart } from '../contexts/CartContext';
import { FREE_SHIPPING_THRESHOLD } from '../lib/shop';
import { INTERNATIONAL_SHIPPING_RATE } from '../lib/shipping';
import { ConfigurationSummary, isMadeToOrder } from '../utils/orderPresentation';
import { characterName } from '../lib/characters';
import { useCopy, useLocale } from '../i18n/LocaleContext';
import './CartSheetAtelier.css';

const COPY = {
  fr: {
    close: 'Fermer le panier', title: 'Mon Placard', count: n => `(${n} article${n > 1 ? 's' : ''})`, description: 'Récapitulatif des articles de votre placard.',
    empty: 'Votre placard est vide', emptyText: 'Découvrez les modèles ou composez votre marque-page.', browse: 'Découvrir la boutique',
    remove: name => `Retirer ${name} du panier`, decrease: name => `Diminuer la quantité de ${name}`, increase: name => `Augmenter la quantité de ${name}`,
    onlyOne: 'Pièce unique — il n’en existe qu’une', subtotal: 'Sous-total', shipping: 'Livraison en France', free: 'Offerte', abroad: amount => `Hors de France : ${amount}`,
    untilFree: amount => `Plus que ${amount} pour la livraison offerte`, total: 'Total', checkout: amount => `Commander — ${amount}`, continue: 'Continuer la boutique',
  },
  en: {
    close: 'Close your bag', title: 'My Cupboard', count: n => `(${n} item${n > 1 ? 's' : ''})`, description: 'Summary of the items in your cupboard.',
    empty: 'Your cupboard is empty', emptyText: 'Discover the companions and find your bookmark.', browse: 'Discover the shop',
    remove: name => `Remove ${name} from your bag`, decrease: name => `Decrease the quantity of ${name}`, increase: name => `Increase the quantity of ${name}`,
    onlyOne: 'One of a kind — there is only one', subtotal: 'Subtotal', shipping: 'Shipping to France', free: 'Free', abroad: amount => `Outside France: ${amount}`,
    untilFree: amount => `Only ${amount} more for free shipping`, total: 'Total', checkout: amount => `Checkout — ${amount}`, continue: 'Continue shopping',
  },
};

export default function CartSheet({ open, onOpenChange }) {
  const { items, removeItem, updateQty, total, shipping, grandTotal, count } = useCart();
  const c = useCopy(COPY);
  const { locale, lp, money } = useLocale();

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent closeLabel={c.close} className="cart-sheet-atelier w-full sm:max-w-md h-dvh flex flex-col gap-0 bg-[hsl(var(--background))] p-0">
        <SheetHeader className="cart-sheet-atelier__header shrink-0 px-6 pr-20 py-5 border-b border-[hsl(var(--border))]">
          <SheetTitle className="cart-sheet-atelier__title font-serif text-xl text-foreground flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" />
            {c.title}
            {count > 0 && <span className="font-sans text-sm text-[hsl(var(--muted-foreground))] font-normal">{c.count(count)}</span>}
          </SheetTitle>
          <SheetDescription className="sr-only">{c.description}</SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="cart-sheet-atelier__empty min-h-0 flex-1 overflow-y-auto flex flex-col items-center justify-center gap-4 px-6" data-lenis-prevent>
            <div className="w-16 h-16 rounded-full bg-[hsl(var(--muted))] flex items-center justify-center">
              <ShoppingBag className="h-8 w-8 text-[hsl(var(--muted-foreground))]" />
            </div>
            <div className="text-center">
              <p className="font-serif text-lg text-foreground mb-2">{c.empty}</p>
              <p className="font-sans text-sm text-[hsl(var(--muted-foreground))]">{c.emptyText}</p>
            </div>
            <Button asChild className="cart-sheet-atelier__action rounded-full bg-primary px-6 text-primary-foreground font-sans text-sm" data-testid="cart-browse-button">
<Link to={lp('/boutique')} onClick={() => onOpenChange(false)}>
                {c.browse}
              </Link>
</Button>
          </div>
        ) : (
          <>
            <div className="cart-sheet-atelier__items min-h-0 flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-4" data-lenis-prevent>
              <AnimatePresence>
                {items.map(cartItem => ({ ...cartItem, displayName: characterName(cartItem.slug, cartItem.name, locale) })).map(item => (
                  <motion.div
                    key={item.cartKey || `${item.slug}-${item.color}`}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20, height: 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    className="cart-sheet-atelier__item flex gap-4"
                  >
                    {/* Product thumb */}
                    <div className="cart-sheet-atelier__media w-16 h-16 rounded-xl flex-shrink-0 overflow-hidden bg-secondary">
                      {item.configuration ? <ConfiguredItemArt item={item} />
                        : <ProductVisual media={productMedia({...item,images:item.image ? [item.image] : []}, {locale})[0]} thumbnail />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 break-words">
                          <p className="font-serif text-sm text-[hsl(var(--foreground))] leading-snug">{item.displayName}</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeItem(item.slug, item.color, item.cartKey)}
                          aria-label={c.remove(item.displayName)}
                          className="h-11 w-11 flex items-center justify-center text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--destructive))] transition-colors duration-200 flex-shrink-0"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <ConfigurationSummary configuration={item.configuration} />
                      <div className="flex flex-wrap gap-2 items-center justify-between mt-2">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => updateQty(item.slug, item.color, item.quantity - 1, item.cartKey)}
                            aria-label={c.decrease(item.displayName)}
                            className="w-11 h-11 rounded-full border border-[hsl(var(--border))] flex items-center justify-center hover:bg-[hsl(var(--muted))] transition-colors duration-200"
                          >
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="font-sans text-sm font-medium w-4 text-center">{item.quantity}</span>
                          <button
                            type="button"
                            onClick={() => updateQty(item.slug, item.color, item.quantity + 1, item.cartKey)}
                            aria-label={c.increase(item.displayName)}
                            disabled={!isMadeToOrder(item) && item.quantity >= 1}
                            title={!isMadeToOrder(item) ? c.onlyOne : undefined}
                            className="w-11 h-11 rounded-full border border-[hsl(var(--border))] flex items-center justify-center hover:bg-[hsl(var(--muted))] transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                        <span className="font-sans text-sm font-semibold text-foreground">{money(item.price * item.quantity)}</span>
                      </div>
                      {!isMadeToOrder(item) && (
                        <p className="mt-1.5 font-sans text-[11px] text-[hsl(var(--muted-foreground))]">{c.onlyOne}</p>
                      )}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            <div className="cart-sheet-atelier__summary shrink-0 max-h-[55dvh] overflow-y-auto border-t border-[hsl(var(--border))] px-6 py-5 space-y-4" data-lenis-prevent>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="font-sans text-sm text-[hsl(var(--muted-foreground))]">{c.subtotal}</span>
                  <span className="font-sans text-sm font-medium">{money(total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-sans text-sm text-[hsl(var(--muted-foreground))]">{c.shipping}</span>
                  <span className="font-sans text-sm font-medium">{shipping === 0 ? c.free : money(shipping)}</span>
                </div>
                {shipping > 0 && <p className="font-sans text-xs text-[hsl(var(--muted-foreground))]">{c.abroad(money(INTERNATIONAL_SHIPPING_RATE))}</p>}
                {shipping > 0 && (
                  <p className="font-sans text-xs text-[hsl(var(--brand-fuchsia-ink))]">{c.untilFree(money(FREE_SHIPPING_THRESHOLD - total))}</p>
                )}
                <Separator />
                <div className="flex justify-between">
                  <span className="font-sans text-base font-semibold">{c.total}</span>
                  <span className="font-sans text-base font-semibold text-foreground">{money(grandTotal)}</span>
                </div>
              </div>
              <Button asChild className="cart-sheet-atelier__action min-h-11 w-full rounded-full bg-primary text-primary-foreground font-sans text-sm font-medium shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-ambient)] transition-[box-shadow,background-color] duration-200" data-testid="cart-checkout-button">
<Link to={lp('/checkout')} onClick={() => onOpenChange(false)}>
                  {c.checkout(money(grandTotal))}
                </Link>
</Button>
              <Link to={lp('/boutique')} onClick={() => onOpenChange(false)} className="flex min-h-11 items-center justify-center text-center font-sans text-sm text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))] transition-colors duration-200">
                {c.continue}
              </Link>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
